import React, { useState } from 'react';
import { FormGroup, Label, Input, Row, Col, FormFeedback } from 'reactstrap';
import FilesComponent from '../../CallForms/Component/FilesComponent';
import { normalizeFieldValue, REGEX, validateEmail, validateIban, validatePhoneTr, displayDecimal } from '../../../../_helper/regexHandler';

const CustomerInfo = ({
    customerId,
    formData = {},
    errors = {},
    handleInputChange,
    handleCustomerTypeChange,
    cities = [],
    districts = [],
    selectedCity,
    setSelectedCity,
    setErrors
}) => {
    formData.customerType = formData.customerType || 'Bireysel';
    formData.contractOriginalArrived = formData.contractOriginalArrived || false;
    
    // State for handling contract files for the customer
    const [, setContractFiles] = useState([]);

    const getIbanError = (value) => {
        if (!value) return '';
        const result = validateIban(value);
        return result.isValid ? '' : (result.error || 'Geçerli bir IBAN formatı giriniz');
    };

    const validateField = (name, value) => {
        let error = '';
        const trimmedValue = typeof value === 'string' ? value?.trim() : value;

        switch (name) {
            case 'clientName':
                if (formData.customerType === 'Bireysel' && !trimmedValue) {
                    error = 'Müvekkil adı zorunludur';
                } else if (trimmedValue && typeof trimmedValue === 'string' && !REGEX.FULL_NAME_UPPER_TR.test(trimmedValue)) {
                    error = 'Müvekkil adı tamamen büyük harflerden oluşmalı ve en az iki kelime içermelidir';
                }
                break;
            case 'identityNumber':
                if (formData.customerType === 'Bireysel' && !trimmedValue) {
                    error = 'TC Kimlik Numarası zorunludur';
                } else if (value && value.length !== 11) {
                    error = 'TC Kimlik Numarası 11 haneli olmalıdır';
                }
                break;
            case 'companyName':
                if (formData.customerType === 'Kurumsal' && !trimmedValue) {
                    error = 'Şirket adı zorunludur';
                }
                break;
            case 'crsNumber':
                if (formData.customerType === 'Kurumsal' && !trimmedValue) {
                    error = 'MERSIS numarası zorunludur';
                }
                break;
            case 'taxNumber':
                if (formData.customerType === 'Kurumsal' && !trimmedValue) {
                    error = 'Vergi numarası zorunludur';
                }
                break;
            case 'companyRepresentative':
                if (formData.customerType === 'Kurumsal' && !trimmedValue) {
                    error = 'Firma yetkilisi zorunludur';
                }
                break;
            case 'email':
                if (formData.customerType === 'Kurumsal' && !trimmedValue) {
                    error = 'E-mail zorunludur';
                } else if (value && !validateEmail(value)) {
                    error = 'Geçerli bir e-mail adresi giriniz';
                }
                break;
            case 'phoneDescription':
                if (!trimmedValue) {
                    error = 'Telefon açıklaması zorunludur';
                }
                break;
            case 'phoneNumber':
                if (!trimmedValue) {
                    error = 'Telefon numarası zorunludur';
                } else if (!validatePhoneTr(value)) {
                    error = 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır';
                }
                break;
            case 'ibanDescription':
                if (!trimmedValue) {
                    error = 'İban açıklaması zorunludur';
                }
                break;
            case 'iban':
                if (!trimmedValue) {
                    error = 'İban zorunludur';
                } else {
                    const ibanValidation = validateIban(value);
                    if (!ibanValidation.isValid) {
                        error = ibanValidation.error || 'Geçerli bir IBAN formatı giriniz';
                    }
                }
                break;
            case 'city':
                if (!selectedCity) {
                    error = 'İl seçimi zorunludur';
                }
                break;
            case 'district':
                if (!value) {
                    error = 'İlçe seçimi zorunludur';
                }
                break;
            case 'address':
                if (!trimmedValue) {
                    error = 'Adres zorunludur';
                }
                break;
            case 'reference':
                if (trimmedValue && typeof trimmedValue === 'string' && trimmedValue.length < 2) {
                    error = 'Referans adı en az 2 karakter olmalıdır.';
                }
                break;
            case 'contractOriginalArrived':
                // Boolean değer için herhangi bir validation gerekmez
                break;
            default:
                break;
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const normalizedValue = typeof value === 'string' ? normalizeFieldValue(name, value) : value;
        const error = validateField(name, normalizedValue);

        handleInputChange({ target: { name, value: normalizedValue } });

        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // FilesComponent handler for contract files
    const handleContractFilesChange = (files) => {
        setContractFiles(files);
    };

    return (
        <>
            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="customerType">Müşteri Türü <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="customerType"
                            name="customerType"
                            value={formData.customerType}
                            onChange={handleCustomerTypeChange}
                        >
                            <option value="Bireysel">Bireysel</option>
                            <option value="Kurumsal">Kurumsal</option>
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="reference">Kimin Referansı?</Label>
                        <Input
                            type="text"
                            id="reference"
                            name="reference"
                            value={formData.reference || ''}
                            onChange={handleChange}
                            placeholder="Referans Adı Soyadı (Opsiyonel)"
                            invalid={!!errors.reference}
                        />
                        <FormFeedback>{errors.reference}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="recipientOfPowerOfAttorney">Vekaleti Alan <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="recipientOfPowerOfAttorney"
                            name="recipientOfPowerOfAttorney"
                            value={formData.agent ? `${formData.agent.firstName} ${formData.agent.lastName}` : ''}
                            disabled={true}
                            style={{ backgroundColor: '#e9ecef' }}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="registrationDate">Kayıt Tarihi <span className="text-danger">*</span></Label>
                        <Input
                            type="datetime-local"
                            id="registrationDate"
                            name="registrationDate"
                            value={formData.created || ''}
                            disabled={true}
                            style={{ backgroundColor: '#e9ecef' }}
                        />
                    </FormGroup>
                </Col>
            </Row>

            {/* Conditional Rendering: Müvekkil Fields */}
            {formData.customerType === 'Bireysel' && (
                <Row form={true}>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="clientName">Müvekkil Adı <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="clientName"
                                name="clientName"
                                value={formData.clientName || ''}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('clientName', e.target.value);
                                    handleChange({
                                        target: {
                                            name: 'clientName',
                                            value
                                        }
                                    });
                                }}
                                onBlur={(e) => {
                                    const finalValue = e.target.value.trim();
                                    if (finalValue !== formData.clientName) {
                                        handleChange({
                                            target: {
                                                name: 'clientName',
                                                value: finalValue
                                            }
                                        });
                                    }
                                }}
                                placeholder="AD SOYAD"
                                invalid={!!errors.clientName || (formData.clientName && !REGEX.FULL_NAME_UPPER_TR.test(formData.clientName.trim()))}
                            />
                            <FormFeedback>
                                {errors.clientName || 'Müvekkil adı tamamen büyük harflerden oluşmalı ve en az iki kelime içermelidir'}
                            </FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="identityNumber">TC Kimlik Numarası <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="identityNumber"
                                name="identityNumber"
                                value={formData.identityNumber || ''}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('identityNumber', e.target.value);
                                    handleChange({
                                        target: {
                                            name: 'identityNumber',
                                            value
                                        }
                                    });
                                }}
                                maxLength={11}
                                placeholder="11 Haneli TC Kimlik No"
                                invalid={!!errors.identityNumber || (formData.identityNumber && formData.identityNumber.length !== 11)}
                            />
                            <FormFeedback>
                                {errors.identityNumber || (formData.identityNumber && formData.identityNumber.length !== 11 ? 'TC Kimlik Numarası 11 haneli olmalıdır' : '')}
                            </FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {/* Conditional Rendering: Şirket Fields */}
            {formData.customerType === 'Kurumsal' && (
                <Row form={true}>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="companyName">Şirket Adı <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                invalid={!!errors.companyName}
                            />
                            <FormFeedback>{errors.companyName}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="crsNumber">MERSIS Numarası <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="crsNumber"
                                name="crsNumber"
                                value={formData.crsNumber}
                                onChange={handleChange}
                                invalid={!!errors.crsNumber}
                            />
                            <FormFeedback>{errors.crsNumber}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="taxNumber">Vergi Numarası <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="taxNumber"
                                name="taxNumber"
                                value={formData.taxNumber}
                                onChange={handleChange}
                                invalid={!!errors.taxNumber}
                            />
                            <FormFeedback>{errors.taxNumber}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {formData.customerType === 'Kurumsal' && (
                <>
                    <Row form={true}>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="companyRepresentative">Firma Yetkilisi <span className="text-danger">*</span></Label>
                                <Input
                                    id="companyRepresentative"
                                    name="companyRepresentative"
                                    value={formData.companyRepresentative || ''}
                                    onChange={handleChange}
                                    invalid={!!errors.companyRepresentative}
                                />
                                <FormFeedback>{errors.companyRepresentative}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row form={true}>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="emailDescription">E-Mail Açıklaması <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="emailDescription"
                                    name="emailDescription"
                                    value={formData.emailDescription || ''}
                                    onChange={handleChange}
                                    placeholder="Örn: İş E-postası"
                                    invalid={!!errors.emailDescription}
                                />
                                <FormFeedback>{errors.emailDescription}</FormFeedback>
                            </FormGroup>
                            <FormGroup>
                                <Label for="email">E-Mail <span className="text-danger">*</span></Label>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email || ''}
                                    onChange={handleChange}
                                    invalid={!!errors.email}
                                />
                                <FormFeedback>{errors.email}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="emailDescription2">E-Mail 2 Açıklaması</Label>
                                <Input
                                    type="text"
                                    id="emailDescription2"
                                    name="emailDescription2"
                                    value={formData.emailDescription2 || ''}
                                    onChange={handleChange}
                                    placeholder="Örn: Kişisel E-posta"
                                    invalid={!!errors.emailDescription2}
                                />
                                <FormFeedback>{errors.emailDescription2}</FormFeedback>
                            </FormGroup>
                            <FormGroup>
                                <Label for="email2">E-Mail 2</Label>
                                <Input
                                    type="email"
                                    id="email2"
                                    name="email2"
                                    value={formData.email2 || ''}
                                    onChange={handleChange}
                                    invalid={!!errors.email2}
                                />
                                <FormFeedback>{errors.email2}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            {/* Boş bırakıldı, düzeni korumak için */}
                        </Col>
                    </Row>
                </>
            )}

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="phoneDescription">Telefon Açıklaması <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="phoneDescription"
                            name="phoneDescription"
                            value={formData.phoneDescription}
                            onChange={handleChange}
                            placeholder="Örn: Ana Telefon"
                            invalid={!!errors.phoneDescription}
                        />
                        <FormFeedback>{errors.phoneDescription}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber">Telefon Numarası <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={(e) => {
                                let value = normalizeFieldValue('phoneNumber', e.target.value);
                                if (value && !value.startsWith('05')) {
                                    if (value.startsWith('5')) {
                                        value = '0' + value;
                                    } else {
                                        value = '';
                                    }
                                }
                                handleChange({
                                    target: {
                                        name: 'phoneNumber',
                                        value
                                    }
                                });
                            }}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                                invalid={!!errors.phoneNumber || (formData.phoneNumber && formData.phoneNumber.length > 0 && !validatePhoneTr(formData.phoneNumber))}
                        />
                        <FormFeedback>
                            {errors.phoneNumber || 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır'}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="phoneDescription2">Telefon 2 Açıklaması</Label>
                        <Input
                            type="text"
                            id="phoneDescription2"
                            name="phoneDescription2"
                            value={formData.phoneDescription2}
                            onChange={handleChange}
                            placeholder="Örn: Yedek Telefon"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber2">Telefon Numarası 2</Label>
                        <Input
                            type="text"
                            id="phoneNumber2"
                            name="phoneNumber2"
                            value={formData.phoneNumber2}
                            onChange={(e) => {
                                let value = normalizeFieldValue('phoneNumber2', e.target.value);
                                if (value && !value.startsWith('05')) {
                                    if (value.startsWith('5')) {
                                        value = '0' + value;
                                    } else {
                                        value = '';
                                    }
                                }
                                handleChange({
                                    target: {
                                        name: 'phoneNumber2',
                                        value
                                    }
                                });
                            }}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                                invalid={!!errors.phoneNumber2 || (formData.phoneNumber2 && formData.phoneNumber2.length > 0 && !validatePhoneTr(formData.phoneNumber2))}
                        />
                        <FormFeedback>
                            {errors.phoneNumber2 || 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır'}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="ibanDescription">İban Açıklaması <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="ibanDescription"
                            name="ibanDescription"
                            value={formData.ibanDescription}
                            onChange={handleChange}
                            placeholder="Örn: Sterlin"
                            invalid={!!errors.ibanDescription}
                        />
                        <FormFeedback>{errors.ibanDescription}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="iban">İban <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="iban"
                            name="iban"
                            value={formData.iban}
                            onChange={(e) => {
                                let value = normalizeFieldValue('iban', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'iban',
                                        value
                                    }
                                });
                            }}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                                invalid={!!errors.iban || !!getIbanError(formData.iban)}
                        />
                        <FormFeedback>
                                {errors.iban || getIbanError(formData.iban)}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="ibanDescription2">İban 2 Açıklaması</Label>
                        <Input
                            type="text"
                            id="ibanDescription2"
                            name="ibanDescription2"
                            value={formData.ibanDescription2}
                            onChange={handleChange}
                            placeholder="Örn: TL Hesabı"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="iban2">İban 2</Label>
                        <Input
                            type="text"
                            id="iban2"
                            name="iban2"
                            value={formData.iban2}
                            onChange={(e) => {
                                let value = normalizeFieldValue('iban2', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'iban2',
                                        value
                                    }
                                });
                            }}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                                invalid={!!errors.iban2 || !!getIbanError(formData.iban2)}
                        />
                        <FormFeedback>
                                {errors.iban2 || getIbanError(formData.iban2)}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="contractRate">Sözleşme Oranı (%) <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="contractRate"
                            name="contractRate"
                            value={displayDecimal(formData.contractRate || '')}
                            onChange={(e) => {
                                const value = normalizeFieldValue('contractRate', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'contractRate',
                                        value: value || null
                                    }
                                });
                            }}
                            placeholder="0-100"
                            invalid={!!errors.contractRate}
                        />
                        <FormFeedback>{errors.contractRate}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="contractOriginalArrived" className="d-block mb-3">
                            Sözleşme Aslı Geldi mi?
                        </Label>
                        <div className="d-flex align-items-center">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="contractOriginalArrivedTrue"
                                    type="radio"
                                    name="contractOriginalArrived"
                                    checked={formData.contractOriginalArrived === true}
                                    onChange={() => handleChange({
                                        target: {
                                            name: 'contractOriginalArrived',
                                            value: true
                                        }
                                    })}
                                    invalid={!!errors.contractOriginalArrived}
                                />
                                <Label for="contractOriginalArrivedTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="contractOriginalArrivedFalse"
                                    type="radio"
                                    name="contractOriginalArrived"
                                    checked={formData.contractOriginalArrived === false}
                                    onChange={() => handleChange({
                                        target: {
                                            name: 'contractOriginalArrived',
                                            value: false
                                        }
                                    })}
                                    invalid={!!errors.contractOriginalArrived}
                                />
                                <Label for="contractOriginalArrivedFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                            <FormFeedback>{errors.contractOriginalArrived}</FormFeedback>
                        </div>
                    </FormGroup>
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="enforcementAttorneyFeeShareRate">İcra Vekalet Hakediş Oranı (%)</Label>
                        <Input
                            type="text"
                            id="enforcementAttorneyFeeShareRate"
                            name="enforcementAttorneyFeeShareRate"
                            value={displayDecimal(formData.enforcementAttorneyFeeShareRate || '')}
                            onChange={(e) => {
                                const value = normalizeFieldValue('enforcementAttorneyFeeShareRate', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'enforcementAttorneyFeeShareRate',
                                        value: value || null
                                    }
                                });
                            }}
                            placeholder="0-100"
                            invalid={!!errors.enforcementAttorneyFeeShareRate}
                        />
                        <FormFeedback>{errors.enforcementAttorneyFeeShareRate}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="judgmentAttorneyFeeShareRate">İlam Vekalet Hakediş Oranı (%)</Label>
                        <Input
                            type="text"
                            id="judgmentAttorneyFeeShareRate"
                            name="judgmentAttorneyFeeShareRate"
                            value={displayDecimal(formData.judgmentAttorneyFeeShareRate || '')}
                            onChange={(e) => {
                                const value = normalizeFieldValue('judgmentAttorneyFeeShareRate', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'judgmentAttorneyFeeShareRate',
                                        value: value || null
                                    }
                                });
                            }}
                            placeholder="0-100"
                            invalid={!!errors.judgmentAttorneyFeeShareRate}
                        />
                        <FormFeedback>{errors.judgmentAttorneyFeeShareRate}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="payInterestToCustomer" className="d-block mb-3">
                            Müşteriye Faiz Ödenecek mi?
                        </Label>
                        <div className="d-flex align-items-center">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="payInterestToCustomerTrue"
                                    type="radio"
                                    name="payInterestToCustomer"
                                    checked={formData.payInterestToCustomer === true}
                                    onChange={() => handleChange({
                                        target: {
                                            name: 'payInterestToCustomer',
                                            value: true
                                        }
                                    })}
                                    invalid={!!errors.payInterestToCustomer}
                                />
                                <Label for="payInterestToCustomerTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="payInterestToCustomerFalse"
                                    type="radio"
                                    name="payInterestToCustomer"
                                    checked={formData.payInterestToCustomer === false}
                                    onChange={() => handleChange({
                                        target: {
                                            name: 'payInterestToCustomer',
                                            value: false
                                        }
                                    })}
                                    invalid={!!errors.payInterestToCustomer}
                                />
                                <Label for="payInterestToCustomerFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                            <FormFeedback>{errors.payInterestToCustomer}</FormFeedback>
                        </div>
                    </FormGroup>
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="middleman">Aracı</Label>
                        <Input
                            type="text"
                            id="middleman"
                            name="middleman"
                            value={formData.middleman}
                            onChange={(e) => {
                                const value = normalizeFieldValue('middleman', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'middleman',
                                        value
                                    }
                                });
                            }}
                            placeholder="Ad Soyad"
                                invalid={!!errors.middleman || (formData.middleman && formData.middleman.length > 0 && !REGEX.FULL_NAME_TITLE_TR.test(formData.middleman))}
                        />
                        <FormFeedback>
                            {errors.middleman || 'Ad ve soyad büyük harfle başlamalı ve en az iki kelimeden oluşmalıdır'}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="middlemanIban">Aracı İban</Label>
                        <Input
                            type="text"
                            id="middlemanIban"
                            name="middlemanIban"
                            value={formData.middlemanIban}
                            onChange={(e) => {
                                let value = normalizeFieldValue('middlemanIban', e.target.value);
                                handleChange({
                                    target: {
                                        name: 'middlemanIban',
                                        value
                                    }
                                });
                            }}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                                invalid={!!errors.middlemanIban || !!getIbanError(formData.middlemanIban)}
                        />
                        <FormFeedback>
                                {errors.middlemanIban || getIbanError(formData.middlemanIban)}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label>Aracı Sözleşme Türü</Label>
                        <div className="d-flex mt-3">
                            <div className="radio radio-primary me-3 d-flex align-items-center">
                                <Input
                                    id="middlemanContractTypeRate"
                                    type="radio"
                                    name="isMiddlemanContractBased"
                                    value="true"
                                    checked={formData.isMiddlemanContractBased === true || formData.isMiddlemanContractBased === "true"}
                                    onChange={() => handleInputChange({ target: { name: 'isMiddlemanContractBased', value: true } })}
                                />
                                <Label for="middlemanContractTypeRate" className="ms-1 mb-0">
                                    Oran Bazlı
                                </Label>
                            </div>
                            <div className="radio radio-primary d-flex align-items-center">
                                <Input
                                    id="middlemanContractTypePrice"
                                    type="radio"
                                    name="isMiddlemanContractBased"
                                    value="false"
                                    checked={formData.isMiddlemanContractBased === false || formData.isMiddlemanContractBased === "false"}
                                    onChange={() => handleInputChange({ target: { name: 'isMiddlemanContractBased', value: false } })}
                                />
                                <Label for="middlemanContractTypePrice" className="ms-1 mb-0">
                                    Fiyat Bazlı
                                </Label>
                            </div>
                        </div>
                        {errors.isMiddlemanContractBased && <FormFeedback style={{ display: 'block' }}>{errors.isMiddlemanContractBased}</FormFeedback>}
                    </FormGroup>
                </Col>
                <Col md={6}>
                    {(formData.isMiddlemanContractBased === true || formData.isMiddlemanContractBased === "true") ? (
                        <FormGroup>
                            <Label for="middlemanContractRate">Aracı Sözleşme Oranı (%)</Label>
                            <Input
                                type="text"
                                id="middlemanContractRate"
                                name="middlemanContractRate"
                                value={displayDecimal(formData.middlemanContractRate || '')}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('middlemanContractRate', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'middlemanContractRate',
                                            value: value || null
                                        }
                                    });
                                }}
                                placeholder="0-100"
                                invalid={!!errors.middlemanContractRate}
                            />
                            <FormFeedback>{errors.middlemanContractRate}</FormFeedback>
                        </FormGroup>
                    ) : (
                        <FormGroup>
                            <Label for="middlemanContractPrice">Aracı Sözleşme Fiyatı (₺)</Label>
                            <Input
                                type="text"
                                id="middlemanContractPrice"
                                name="middlemanContractPrice"
                                value={displayDecimal(formData.middlemanContractPrice || '')}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('middlemanContractPrice', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'middlemanContractPrice',
                                            value: value || null
                                        }
                                    });
                                }}
                                placeholder="Örn: 1000,50"
                                invalid={!!errors.middlemanContractPrice}
                            />
                            <FormFeedback>{errors.middlemanContractPrice}</FormFeedback>
                        </FormGroup>
                    )}
                </Col>
            </Row>

            <Row form={true}>
                <Col md={6}>
                    <FormGroup>
                        <Label for="city">İl <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="city"
                            name="city"
                            value={selectedCity || ''}
                            onChange={(e) => {
                                setSelectedCity(e.target.value);
                                handleChange({
                                    target: {
                                        name: 'district',
                                        value: ''
                                    }
                                });
                            }}
                            invalid={!!errors.city}
                        >
                            <option value="">Seçiniz</option>
                            {cities.map(city => (
                                <option key={city.id} value={city.id}>
                                    {city.title}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.city}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="district">İlçe <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="district"
                            name="district"
                            value={formData.district || ''}
                            onChange={handleChange}
                            invalid={!!errors.district}
                            disabled={!selectedCity}
                        >
                            <option value="">Seçiniz</option>
                            {districts.map(district => (
                                <option key={district.id} value={district.id}>
                                    {district.title}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.district}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label for="address">Adres <span className="text-danger">*</span></Label>
                <Input
                    type="textarea"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    style={{
                        height: '100px',
                        resize: 'none',
                        overflowY: 'auto'
                    }}
                    invalid={!!errors.address}
                />
                <FormFeedback>{errors.address}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="notes">Notlar</Label>
                <Input
                    type="textarea"
                    id="notes"
                    name="notes"
                    value={formData.comment}
                    onChange={(e) => {
                        const value = e.target.value.slice(0, 500);
                        handleChange({
                            target: {
                                name: 'comment',
                                value
                            }
                        });
                    }}
                    style={{
                        height: '150px',
                        resize: 'none',
                        overflowY: 'auto'
                    }}
                    maxLength={500}
                    placeholder="En fazla 500 karakter"
                    invalid={!!errors.comment}
                />
                <small className="text-muted">{formData.comment ? formData.comment.length : 0}/500</small>
                <FormFeedback>{errors.comment}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <FilesComponent
                    onFilesChange={handleContractFilesChange}
                    multiple={false}
                    callFormReference={false}
                    referenceId={customerId}
                    typeFilter={['SÖZLEŞME']}
                    title="Sözleşme"
                    canEdit={true}
                />
            </FormGroup>
        </>
    );
};

export default CustomerInfo;
